import { useState, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, FolderKanban, Users, Receipt, FileText, Wrench, Store,
  FolderOpen, ShoppingCart, Loader2, X
} from "lucide-react";
import { useLocation } from "wouter";

const TYPE_CONFIG: Record<string, { icon: typeof Search; label: string; color: string }> = {
  project: { icon: FolderKanban, label: "Project", color: "bg-blue-100 text-blue-700" },
  lead: { icon: Users, label: "Lead", color: "bg-amber-100 text-amber-700" },
  receipt: { icon: Receipt, label: "Receipt", color: "bg-purple-100 text-purple-700" },
  invoice: { icon: FileText, label: "Invoice", color: "bg-emerald-100 text-emerald-700" },
  subcontractor: { icon: Wrench, label: "Sub", color: "bg-orange-100 text-orange-700" },
  vendor: { icon: Store, label: "Vendor", color: "bg-cyan-100 text-cyan-700" },
  document: { icon: FolderOpen, label: "Document", color: "bg-slate-100 text-slate-700" },
  purchase_order: { icon: ShoppingCart, label: "PO", color: "bg-pink-100 text-pink-700" },
};

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [, setLocation] = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: results = [], isLoading } = trpc.search.global.useQuery(
    { query: debouncedQuery, limit: 15 },
    { enabled: debouncedQuery.length >= 2 }
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = (result: any) => {
    setLocation(result.link);
    setIsOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder="Search everything... (⌘K)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-8 pr-8 h-8 w-48 md:w-64 lg:w-80 text-sm bg-muted/50 border-none focus-visible:ring-1"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setIsOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {isOpen && debouncedQuery.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover text-popover-foreground border rounded-lg shadow-lg z-50 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No results for "{debouncedQuery}"
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y">
              {results.map((result: any, i: number) => {
                const config = TYPE_CONFIG[result.type] || { icon: Search, label: result.type, color: "bg-gray-100 text-gray-700" };
                const Icon = config.icon;
                return (
                  <div
                    key={`${result.type}-${result.id}-${i}`}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleSelect(result)}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                      )}
                    </div>
                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 shrink-0 ${config.color}`}>
                      {config.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
